import React, { useState, useEffect } from 'react';
import { FaGithub, FaExternalLinkAlt, FaCode, FaFire, FaChartLine } from 'react-icons/fa';
import config from '../config';
import './Experience.css';

const LEETCODE_USERNAME = 'MALIPEDDI_SEKHAR';

function Experience() {
  const [leetcodeStats, setLeetcodeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeJourneyIndex, setActiveJourneyIndex] = useState(0);
  const [flowSpinClass, setFlowSpinClass] = useState('');
  const [journeyPulseClass, setJourneyPulseClass] = useState('journey-pulse-a');

  const handleJourneyNodeClick = (index) => {
    setActiveJourneyIndex(index);
    setFlowSpinClass((prev) => (prev === 'flow-spin-a' ? 'flow-spin-b' : 'flow-spin-a'));
    setJourneyPulseClass((prev) => (prev === 'journey-pulse-a' ? 'journey-pulse-b' : 'journey-pulse-a'));
  };

  const journeyFlow = [
    {
      phase: 'Foundation Phase',
      year: '2021 - 2024',
      title: 'Diploma in Computer Science',
      summary: 'Built strong programming fundamentals and started my coding journey with Java and Python while understanding how software works from the ground up.',
      tech: ['Java', 'Python', 'Programming Fundamentals', 'Computer Science Basics']
    },
    {
      phase: 'Problem Solving Phase',
      year: '2024',
      title: 'DSA and Developer Workflow',
      summary: 'Focused on data structures, algorithms, and clean problem-solving patterns while practicing version control and collaborative coding workflows.',
      tech: ['DSA', 'OOP', 'Git', 'GitHub', 'Debugging']
    },
    {
      phase: 'AI Learning Phase',
      year: '2024 - 2025',
      title: 'AI and ML Exploration',
      summary: 'Expanded into artificial intelligence by learning machine learning pipelines, neural networks, deep learning basics, and data-driven thinking.',
      tech: ['AI', 'ML', 'AIML', 'Neural Networks', 'Deep Learning', 'Data Analysis']
    },
    {
      phase: 'Full Stack Phase',
      year: '2025',
      title: 'Java Full Stack Development',
      summary: 'Started building complete applications using Spring Boot on backend and React on frontend with MySQL databases and robust REST APIs.',
      tech: ['Java Full Stack', 'Spring Boot', 'React.js', 'MySQL', 'REST APIs', 'Authentication']
    },
    {
      phase: 'Build and Scale Phase',
      year: '2025 - Present',
      title: 'Production Mindset and Impact',
      summary: 'Now focused on building scalable, practical, and user-centric products with strong architecture, performance, and real-world usability.',
      tech: ['System Design', 'UI/UX', 'Performance', 'Testing', 'Deployment', 'Team Collaboration']
    }
  ];

  // Fallback stats - update these manually when your actual stats change
  const fallbackStats = {
    totalSolved: 50,
    easySolved: 25,
    mediumSolved: 20,
    hardSolved: 5,
    ranking: 0
  };

  useEffect(() => {
    const fetchLeetCodeStats = async () => {
      try {
        const response = await fetch(`${config.API_URL}/api/leetcode/${encodeURIComponent(LEETCODE_USERNAME)}`, {
          method: 'GET',
          headers: { Accept: 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          setLeetcodeStats({
            totalSolved: data.totalSolved || data.solvedProblem || fallbackStats.totalSolved,
            easySolved: data.easySolved || fallbackStats.easySolved,
            mediumSolved: data.mediumSolved || fallbackStats.mediumSolved,
            hardSolved: data.hardSolved || fallbackStats.hardSolved,
            ranking: data.ranking || 0
          });
        } else {
          setLeetcodeStats(fallbackStats);
        }
      } catch (error) {
        setLeetcodeStats(fallbackStats);
      } finally {
        setLoading(false);
      }
    };

    fetchLeetCodeStats();
  }, []);

  const experiences = [
    {
      role: 'SDE Intern',
      company: 'Chitral AI',
      companyUrl: 'https://getflashman.com/',
      period: 'Aug 2026 – Present',
      current: true,
      description: [
        'Working as a Software Development Engineer Intern on real-world web application development in a fast-paced AI startup environment',
        'Developing and enhancing web applications using Flask and related backend technologies aligned with product requirements',
        'Building and integrating application features, RESTful APIs, and backend functionality for production-grade systems',
        'Working on payment integration workflows, transaction handling, and end-to-end integration testing',
        'Performing functional testing, debugging, and troubleshooting to identify and resolve application issues',
        'Collaborating with the development team to deliver reliable, production-ready features on schedule'
      ],
      projects: []
    },
    {
      role: 'Full Stack Developer Intern',
      company: 'Avijo',
      companyUrl: 'https://www.avijo.in/',
      period: 'May 2026 – Jul 2026',
      current: false,
      description: [
        'Contributed to the development of a real-world healthcare platform in a high-impact startup environment',
        'Developed and enhanced interactive dashboards for managing and visualizing healthcare-related data',
        'Designed and integrated RESTful APIs to support seamless communication between frontend applications and backend services',
        'Worked on real-time application features and improved overall platform functionality and user experience',
        'Collaborated with the development team on feature implementation, debugging, testing, and API integration',
        'Followed production-oriented development practices while contributing to an actively developed healthcare product'
      ],
      projects: []
    },
    {
      role: 'React JS Developer Intern',
      company: 'UpToSkills',
      period: 'Jan 2026 – Apr 2026',
      current: false,
      description: [
        'Developed modern and responsive web applications using React.js with a focus on reusable component architecture',
        'Built an AI Resume Builder using React.js with AI-powered features for automated, intelligent resume generation',
        'Implemented state management using Redux and Context API, optimizing application performance and maintainability',
        'Collaborated with cross-functional teams in Agile sprints, participating in daily standups and peer code reviews',
        'Used Git/GitHub for version control, branch management, and collaborative development workflows'
      ],
      projects: [
        {
          name: 'AI Resume Builder',
          github: 'https://github.com/malipeddisekhar/AI-Resume-Builder'
        }
      ]
    },
    {
      role: 'Java Full Stack Developer Intern',
      company: 'Bytexl',
      period: '2024 – 2025',
      current: false,
      description: [
        'Developed full-stack web applications using Spring Boot, React.js, and MySQL in an Agile team environment',
        'Built Campus Connect — a comprehensive college management platform supporting students and faculty with role-based access control and secure authentication',
        'Designed and developed RESTful APIs using Spring Boot, integrating MySQL databases with efficient query optimization',
        'Participated in Agile development cycles including sprint planning, debugging, testing, code reviews, and documentation',
        'Used Git/GitHub for version control and collaborative team development'
      ],
      projects: [
        {
          name: 'Campus Connect',
          github: 'https://github.com/malipeddisekhar/CampusConnect'
        }
      ]
    }
  ];


  return (
    <section className="experience section">
      <div className="container">
        {/* Journey Flow */}
        <div className="journey-flow-section">
          <h2 className="section-title">Journey Flow</h2>
          <p className="journey-flow-intro journey-flow-quote">
            "I did not wait for opportunities - I engineered them. From diploma roots to full stack and AI systems, every stage of my journey turned learning into real impact."
          </p>
          <div className={`journey-flow-track ${flowSpinClass}`}>
            {journeyFlow.map((step, index) => (
              <article
                key={step.title}
                className={`journey-node ${index % 2 === 0 ? 'left' : 'right'}`}
                style={{ '--node-index': index }}
              >
                <span className="journey-node-dot" aria-hidden="true"></span>
                <div
                  className={`journey-node-card card ${activeJourneyIndex === index ? `active ${journeyPulseClass}` : ''}`}
                  onClick={() => handleJourneyNodeClick(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleJourneyNodeClick(index);
                    }
                  }}
                >
                  <div className="journey-node-meta">
                    <span className="journey-phase">{step.phase}</span>
                    <span className="journey-year">{step.year}</span>
                  </div>
                  <h3 className="journey-title">{step.title}</h3>
                  <p className="journey-summary">{step.summary}</p>
                  <div className="journey-tech-stack">
                    {step.tech.map((item) => (
                      <span key={item} className="journey-tech-tag">{item}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Professional Experience */}
        <h2 className="section-title">Professional Experience</h2>
        <div className="exp-cards">
          {experiences.map((exp, index) => (
            <div key={index} className={`exp-card card ${exp.current ? 'current-exp' : ''}`}>
              <div className="exp-header">
                <div>
                  <h3 className="exp-role">{exp.role}</h3>
                  <p className="exp-company">
                    {exp.companyUrl ? (
                      <a href={exp.companyUrl} target="_blank" rel="noopener noreferrer" className="exp-company-link">
                        {exp.company} <FaExternalLinkAlt style={{ fontSize: '0.65em', verticalAlign: 'middle' }} />
                      </a>
                    ) : (
                      exp.company
                    )}
                  </p>
                </div>
                <div className="exp-period-wrapper">
                  {exp.current && <span className="current-badge">Current</span>}
                  <span className="exp-period">{exp.period}</span>
                </div>
              </div>
              <ul className="exp-list">
                {exp.description.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              {exp.projects && exp.projects.length > 0 && (
                <div className="exp-projects">
                  <h4>Key Projects:</h4>
                  <div className="exp-project-links">
                    {exp.projects.map((proj, i) => (
                      <a 
                        key={i} 
                        href={proj.github} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="exp-project-link"
                      >
                        <FaGithub /> {proj.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* LeetCode Stats */}
        <div className="leetcode-section">
          <h2 className="section-title">
            <FaCode style={{ marginRight: '10px', color: '#00d4ff' }} />
            LeetCode Progress
          </h2>
          <div className="leetcode-card card">
            {loading ? (
              <div className="leetcode-loading">
                <div className="loading-spinner"></div>
                <p>Loading LeetCode stats...</p>
              </div>
            ) : leetcodeStats ? (
              <>
                <div className="leetcode-header">
                  <div className="leetcode-profile">
                    <div className="leetcode-logo">
                      <FaCode />
                    </div>
                    <div>
                      <h3>{LEETCODE_USERNAME}</h3>
                      <a 
                        href={`https://leetcode.com/u/${LEETCODE_USERNAME}/`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="leetcode-link"
                      >
                        View Profile <FaExternalLinkAlt />
                      </a>
                    </div>
                  </div>
                  {leetcodeStats.ranking > 0 && (
                    <div className="leetcode-rank">
                      <FaChartLine />
                      <span>Rank: {leetcodeStats.ranking.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                <div className="leetcode-stats-grid">
                  <div className="leetcode-stat-card total">
                    <div className="stat-number">{leetcodeStats.totalSolved || 0}</div>
                    <div className="stat-label">Problems Solved</div>
                    <div className="stat-icon"><FaFire /></div>
                  </div>
                  
                  <div className="leetcode-stat-card easy">
                    <div className="stat-number">{leetcodeStats.easySolved || 0}</div>
                    <div className="stat-label">Easy</div>
                    <div className="stat-bar">
                      <div className="stat-fill" style={{ width: `${leetcodeStats.easySolved ? (leetcodeStats.easySolved / 830) * 100 : 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div className="leetcode-stat-card medium">
                    <div className="stat-number">{leetcodeStats.mediumSolved || 0}</div>
                    <div className="stat-label">Medium</div>
                    <div className="stat-bar">
                      <div className="stat-fill" style={{ width: `${leetcodeStats.mediumSolved ? (leetcodeStats.mediumSolved / 1742) * 100 : 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div className="leetcode-stat-card hard">
                    <div className="stat-number">{leetcodeStats.hardSolved || 0}</div>
                    <div className="stat-label">Hard</div>
                    <div className="stat-bar">
                      <div className="stat-fill" style={{ width: `${leetcodeStats.hardSolved ? (leetcodeStats.hardSolved / 756) * 100 : 0}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="leetcode-footer">
                  <p><span className="quote-icon">💡</span> "Code is like humor. When you have to explain it, it's bad." — Cory House</p>
                </div>
              </>
            ) : (
              <div className="leetcode-error">
                <p>Unable to load LeetCode stats. <a href={`https://leetcode.com/u/${LEETCODE_USERNAME}/`} target="_blank" rel="noopener noreferrer">Visit Profile</a></p>
              </div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}

export default Experience;
